// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@keller/App/Components/Common/XngZone";
import { constants } from "@keller/XDL/constants";
import { color, hideElement, xScreen } from "@keller/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductListLayoutStyles {
    container?: InjectableCss;
    leftColumnContainer?: InjectableCss;
    rightColumnContainer?: InjectableCss;
    mainSectionWrapper?: InjectableCss;
    productTitleSuggestionWrapper?: InjectableCss;
    mainSectionActionsContainer?: InjectableCss;
    sortFilterButton?: ButtonPresentationProps;
    sortAndPaginationWrapper?: InjectableCss;
    modal?: ModalPresentationProps;
    categorySectionWrapper?: InjectableCss;
}

const sortAndFilterBreakpoint = constants.productList.xSortAndFilter.breakpoint;
export const layoutStyles: ProductListLayoutStyles = {
    container: {
        css: css`
            display: flex;
            gap: 42px;
        `,
    },
    leftColumnContainer: {
        css: css`
            flex: 0 0 288px;
            ${xScreen("lte", sortAndFilterBreakpoint)} {
                ${hideElement}
            }
        `,
    },
    rightColumnContainer: {
        css: css`            
            flex-grow: 1;
            display: flex;
            flex-direction: column;
            gap: 16px;
            width:100%;
            overflow: auto;
            ${xScreen("lte", sortAndFilterBreakpoint)} {
                gap: 24px;
            }
        `,
    },
    mainSectionWrapper: {
        css: css`
            width: 100%;
            ${xScreen("lte", sortAndFilterBreakpoint)} {
                display: flex;
                justify-content: space-between;
                align-items: flex-end;
                gap: 16px;
                padding-bottom: 24px;
                border-bottom: 1px solid ${color("common.border")};
            }
        `,
    },
    mainSectionActionsContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-wrap: wrap;
            padding: 12px 0;
            border-width: 1px 0;
            border-color: ${color("common.border")};
            border-style: solid;

            ${xScreen("lte", sortAndFilterBreakpoint)} {
                ${hideElement}
            }
        `,
    },
    sortFilterButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            ${xScreen("gt", sortAndFilterBreakpoint)} {
                ${hideElement}
            }
        `,
    },
    sortAndPaginationWrapper: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: space-between;
            gap: 12px 20px;
            border: 1px solid ${color("common.border")};
            background: ${color("common.background")};
            padding: 12px;
            ${xScreen("lte", "xs")} {
                flex-direction: column;
            }
        `,
    },
    categorySectionWrapper:{
        css: css`
            padding-bottom: 20px;
            &:has(> div:empty) {
                display: none;
            }                                    
        `,
    }
};

const styles = layoutStyles;

const ProductListLayout: FC<WidgetProps> = ({ id }) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);

    const modalOpenHandler = () => {
        setModalIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftColumnContainer}>
                <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
            </StyledWrapper>
            <StyledWrapper {...styles.rightColumnContainer}>
                <StyledWrapper {...styles.categorySectionWrapper}>
                    <XngZone contentId={id} zoneName="ContentCategorySection" hint="Categories Section" />
                </StyledWrapper>               
                <StyledWrapper {...styles.mainSectionWrapper}>
                    <StyledWrapper
                        {...styles.productTitleSuggestionWrapper}
                        data-test-selector="productTitle-and-suggestion-productList"
                    >
                        <XngZone contentId={id} zoneName="ContentMainHeader06" hint="Suggestion" normalWidth={true} />
                        <XngZone contentId={id} zoneName="ContentMainHeader00" hint="Count Item" normalWidth={true} />
                    </StyledWrapper>
                    <Button onClick={modalOpenHandler} {...styles.sortFilterButton}>
                        {translate("Sort & Filter")}
                    </Button>
                </StyledWrapper>
                <StyledWrapper {...styles.mainSectionActionsContainer}>
                    <XngZone contentId={id} zoneName="ContentMainHeader01" normalWidth={true} hint="Sort Item" />
                    <XngZone contentId={id} zoneName="ContentMainHeader03" normalWidth={true} hint="View Item" />
                    <XngZone
                        contentId={id}
                        zoneName="ContentMainHeader04"
                        normalWidth={true}
                        hint="Pagination PerPage"
                    />
                    <XngZone contentId={id} zoneName="ContentMainHeader05" normalWidth={true} hint="Pagination Page" />
                </StyledWrapper>
                <XngZone contentId={id} zoneName="ContentMainSection" hint="Main Section" />
            </StyledWrapper>
            <Modal
                {...styles.modal}
                headline={translate("Sort & Filter")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <GridContainer gap={8}>
                    <GridItem width={12}>
                        <StyledWrapper {...styles.sortAndPaginationWrapper}>
                            <XngZone contentId={id} zoneName="ContentMainHeader01" hint="Sort Item" />
                            <XngZone contentId={id} zoneName="ContentMainHeader04" hint="Pagination PerPage" />
                        </StyledWrapper>
                    </GridItem>
                    <GridItem width={12}>
                        <XngZone contentId={id} zoneName="ContentFilter" hint="Filter Section" />
                    </GridItem>
                </GridContainer>
            </Modal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: ProductListLayout,
    definition: {
        group: "Product List",
        displayName: "ProductList Layout",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
